/**
 * The grid — `docs/activity.md` §6.1 (Board 2): the whole day, 12 AM to 12 AM
 * (Aaron, Oct 23: "scroll on the calendar from 12 to 12" — some people keep
 * everything here), at a pixel a minute, an all-day row, day headers all one
 * height, today tinted, the now line on today only, the past a shade greyer
 * over its whole column (today down to the now line), and a day the person
 * does not work dimmed. Columns are days (one person, Combined) or people
 * (Day, side by side); what sits in them is the caller's.
 *
 * With `fill` the card takes the height it is given and the hours scroll
 * inside it under pinned heads, opening at `initialMinutes` (the start of the
 * working day) — the calendar fills the page at every width.
 *
 * With `flush` there is no card at all — no corners, no border — for the
 * phone's Day and 3 days, which run edge to edge (Aaron, Oct 27, notes 1.1.2
 * and 1.2.1: "remove that margin all the way around and expand the content
 * out to both sides and the top and bottom bar").
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import {
  AXIS_WIDTH, GRID_BOTTOM, GRID_TOP, HEAD_HEIGHT, NOW_RED, OFF_TINT, PAST_TINT, PX_PER_MINUTE, hourWords,
} from './look.js'

const hasChildren = (node: ReactNode): boolean =>
  Array.isArray(node) ? node.some(hasChildren) : node !== null && node !== undefined && node !== false

export interface GridColumn {
  key: string
  /** The header's contents (the day, or the person). */
  head: ReactNode
  /** All-day chips. */
  allDay?: ReactNode
  /** Before today: the whole column; today: down to the now line. */
  past: 'all' | 'until-now' | 'none'
  today: boolean
  /** A day the person does not work. */
  off: boolean
  /** Positioned by the caller, inside the hours' box (top 0 = the start hour). */
  body: ReactNode
  /** Mouse down in empty time — a drag to block or a click to add (§6.5). */
  onEmpty?: (minutes: number, event: React.PointerEvent<HTMLDivElement>) => void
}

export function TimeGrid({
  columns, startHour, endHour, now, columnWidth, headClassName,
  scale = PX_PER_MINUTE, axisWidth = AXIS_WIDTH, headHeight = HEAD_HEIGHT, allDayWhenEmpty = true, width, fill = false,
  initialMinutes, scrollTo, halfHours = false, flush = false,
}: {
  columns: GridColumn[]
  startHour: number
  endHour: number
  /** Local minutes now, for the now line (today only). */
  now: number
  /** A fixed width per column (Day side by side: 220); else they share the width. */
  columnWidth?: number
  headClassName?: string
  /** Pixels a minute (`GRID_METRICS`). */
  scale?: number
  axisWidth?: number
  headHeight?: number
  /** The iPad and the phone draw no all-day row when nothing is in it (Boards 12, 13). */
  allDayWhenEmpty?: boolean
  /** A card narrower than its column (Board 12 A: 620 beside the stop panel). */
  width?: number
  /** The card fills the height it is given and the hours scroll inside it, the heads pinned. */
  fill?: boolean
  /** With `fill`: where the hours open (local minutes) — the start of the working day. */
  initialMinutes?: number
  /** With `fill`: scroll so these minutes are in view, again each time `nonce` changes (*Today*). */
  scrollTo?: { minutes: number; nonce: number } | null
  /**
   * The phone's Day and 3 days (Aaron, Oct 25, note 6): a solid line each
   * hour and a fainter one each half hour. Off, the grid is as it always was.
   */
  halfHours?: boolean
  /** No card: no corners and no border, edge to edge (the phone's Day and 3 days); fixed-width columns fill the width when they fit. */
  flush?: boolean
}) {
  const AXIS = axisWidth
  const hours = endHour - startHour
  const height = hours * 60 * scale
  const nowTop = (now - startHour * 60) * scale
  const allDay = allDayWhenEmpty || columns.some((column) => hasChildren(column.allDay))
  const fixed = columnWidth !== undefined
  /*
   * Edge to edge, a fixed width is a least: the columns share what the screen has when they fit and
   * scroll sideways when they do not, so three people on a phone reach its edge rather than stopping
   * short of it with a blank strip beside them.
   */
  const columnStyle = fixed
    ? (flush ? { minWidth: columnWidth, flex: `1 0 ${columnWidth}px` } : { width: columnWidth, flex: `0 0 ${columnWidth}px` })
    : { flex: '1 1 0', minWidth: 0 }
  const fixedWidth = AXIS + columns.length * (columnWidth ?? 0)

  /* Open at the start of the working day: that minute just under the pinned heads. */
  const scroller = useRef<HTMLDivElement>(null)
  const opened = useRef(false)
  useEffect(() => {
    const box = scroller.current
    if (!fill || !box || opened.current || initialMinutes === undefined) return
    opened.current = true
    box.scrollTop = Math.max(0, GRID_TOP + (initialMinutes - startHour * 60) * scale - 6)
  }, [fill, initialMinutes, startHour, scale])
  /* *Today*: now a third of the way down what shows. */
  useEffect(() => {
    const box = scroller.current
    if (!fill || !box || !scrollTo) return
    const pinned = [...box.querySelectorAll<HTMLElement>('[data-grid-pinned]')].reduce((sum, row) => sum + row.offsetHeight, 0)
    const at = GRID_TOP + (scrollTo.minutes - startHour * 60) * scale
    box.scrollTo({ top: Math.max(0, at - (box.clientHeight - pinned) / 3), behavior: 'smooth' })
  }, [fill, scrollTo, startHour, scale])

  return (
    <div className={cn('overflow-hidden bg-surface', !flush && 'rounded-xl border border-line', fill && 'flex min-h-0 flex-1 flex-col')}
      data-grid="calendar" style={width ? { width } : undefined}>
      <div ref={scroller} data-grid-scroll="" className={cn(fixed && 'overflow-x-auto', fill && 'min-h-0 flex-1 overflow-y-auto')}>
        <div style={fixed ? (flush ? { minWidth: fixedWidth } : { width: fixedWidth }) : undefined}>
          {/* Day headers (52 px) and the all-day row stay put while the hours scroll. A head of 0 is none: the phone's strip heads 3 days. */}
          {headHeight > 0 && <div data-grid-pinned="" className={cn('flex bg-surface', fill && 'sticky top-0 z-20', headClassName)}>
            <div className={cn('shrink-0 bg-surface', fixed && 'sticky left-0 z-10')} style={{ width: AXIS }} />
            {columns.map((column) => (
              <div key={column.key} data-column-head="" className="flex items-center justify-center border-l border-line-faint"
                style={{ ...columnStyle, height: headHeight, background: column.past !== 'none' && column.past === 'all' ? PAST_TINT : undefined }}>
                <div className={cn('flex min-w-0 flex-col items-center', column.off && 'opacity-55')}>{column.head}</div>
              </div>
            ))}
          </div>}
          {/* The all-day row */}
          {allDay && <div data-grid-pinned="" className={cn('flex border-b border-t border-line bg-surface', fill && 'sticky z-20')}
            style={{ borderTopColor: 'rgb(var(--color-line-faint))', top: fill ? headHeight : undefined }}>
            <div className={cn('flex shrink-0 items-center justify-end bg-surface pr-2 text-[10px] font-semibold text-text-faint', fixed && 'sticky left-0 z-10')}
              style={{ width: AXIS }}>all day</div>
            {columns.map((column) => (
              <div key={column.key} className="flex min-h-[28px] flex-col gap-0.5 border-l border-line-faint p-[3px]"
                style={{ ...columnStyle, background: column.past === 'all' ? PAST_TINT : undefined }}>
                {column.allDay}
              </div>
            ))}
          </div>}
          {/* The hours */}
          <div style={{ paddingTop: GRID_TOP, paddingBottom: GRID_BOTTOM }}>
            <div className="relative flex" style={{ height }}>
              <div className={cn('relative shrink-0 bg-surface', fixed && 'sticky left-0 z-10')} style={{ width: AXIS }}>
                {/* An hour's words at its line; midnight at the foot is the next day's, so it has none. */}
                {Array.from({ length: hours }, (_, n) => (
                  <div key={n} className="absolute right-2 whitespace-nowrap text-right text-[10.5px] font-semibold text-text-faint"
                    style={{ top: n * 60 * scale - 7 }}>
                    {hourWords(startHour + n)}
                  </div>
                ))}
              </div>
              {!halfHours && Array.from({ length: hours + 1 }, (_, n) => (
                <div key={n} className="pointer-events-none absolute right-0 border-t border-line-faint"
                  style={{ left: AXIS, top: n * 60 * scale }} />
              ))}
              {columns.map((column) => (
                <div key={column.key} data-column={column.key} className="relative border-l border-line-faint"
                  style={{ ...columnStyle, marginTop: -GRID_TOP, height: height + GRID_TOP + GRID_BOTTOM,
                    background: column.today ? 'rgb(var(--color-green-band-bg))' : undefined }}>
                  <div
                    className="absolute inset-x-0"
                    style={{ top: GRID_TOP, height }}
                    onPointerDown={column.onEmpty ? (event) => {
                      if (event.target !== event.currentTarget) return
                      const rect = event.currentTarget.getBoundingClientRect()
                      column.onEmpty!(startHour * 60 + Math.round((event.clientY - rect.top) / scale), event)
                    } : undefined}
                  >
                    {/* Inside the column, so today's tint never covers them and every block sits over them. */}
                    {halfHours && <HourLines hours={hours} scale={scale} />}
                    {column.body}
                    {column.past === 'all' && (
                      <div data-past="all" className="pointer-events-none absolute inset-x-0" style={{ top: -GRID_TOP, bottom: -GRID_BOTTOM, background: PAST_TINT, zIndex: 5 }} />
                    )}
                    {column.past === 'until-now' && nowTop > -GRID_TOP && (
                      <div data-past="until-now" className="pointer-events-none absolute inset-x-0" style={{ top: -GRID_TOP, height: Math.min(nowTop, height) + GRID_TOP, background: PAST_TINT, zIndex: 5 }} />
                    )}
                    {column.off && (
                      <div className="pointer-events-none absolute inset-x-0" style={{ top: -GRID_TOP, bottom: -GRID_BOTTOM, background: OFF_TINT }} />
                    )}
                    {column.today && nowTop >= 0 && nowTop <= height && (
                      <>
                        <div className="pointer-events-none absolute inset-x-0" style={{ top: nowTop, borderTop: `2px solid ${NOW_RED}`, zIndex: 6 }} data-now-line />
                        <div className="pointer-events-none absolute h-2 w-2 rounded-full" style={{ left: -5, top: nowTop - 4, background: NOW_RED, zIndex: 6 }} />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** A solid line each hour and a fainter one each half hour (Aaron, Oct 25, note 6). */
function HourLines({ hours, scale }: { hours: number; scale: number }) {
  return (
    <>
      {Array.from({ length: hours * 2 + 1 }, (_, n) => (
        <div key={n} aria-hidden data-hour-line={n % 2 === 0 ? 'hour' : 'half'}
          className={cn('pointer-events-none absolute inset-x-0 border-t', n % 2 === 0 ? 'border-line' : 'border-line-soft')}
          style={{ top: n * 30 * scale }} />
      ))}
    </>
  )
}
